import { useRef, useState } from 'react';
import { Paper, useMediaQuery, useTheme } from '@mui/material';

/**
 * Dialog paper that can be dragged aside by its title bar on desktop.
 *
 * Workshops edit a part or an invoice line while reading the lines behind the dialog, and the
 * dialog covers exactly those. Wired as the default paper for every dialog in theme.js; the
 * `DialogTitle` becomes the handle, buttons inside it (the close X) still work. On phones the
 * dialog is close to full-screen and touch-dragging fights scrolling, so nothing happens below
 * the md breakpoint, and a full-screen dialog has nowhere to go. Position resets on every open
 * because the paper remounts with the dialog. Applied to every dialog through theme.js.
 */
/** True when the point lies on a glyph of one of the element's own text nodes. */
function isOverText(element, x, y) {
  const range = document.createRange();
  for (const node of element.childNodes) {
    if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) continue;
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) {
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return true;
    }
  }
  return false;
}

export default function DraggableDialogPaper({ sx, style, onPointerDown, ...props }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const fullScreen = (props.className || '').includes('MuiDialog-paperFullScreen');
  const draggable = desktop && !fullScreen;
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Cursor over the title: text over glyphs (selectable), move over padding (drag handle).
  const [overText, setOverText] = useState(false);
  const drag = useRef(null);

  const handlePointerDown = (event) => {
    onPointerDown?.(event);
    if (!draggable || event.button !== 0) return;
    const handle = event.target.closest('.MuiDialogTitle-root');
    if (!handle || !event.currentTarget.contains(handle)) return;
    if (event.target.closest('button, a, input, textarea, select, [role="button"]')) return;
    // Text stays selectable: a Typography inside the title, or the glyphs of a bare-text title,
    // are not part of the handle. Padding and empty space are.
    if (event.target.closest('.MuiDialogTitle-root .MuiTypography-root')) return;
    if (isOverText(event.target, event.clientX, event.clientY)) return;
    drag.current = { startX: event.clientX, startY: event.clientY, baseX: offset.x, baseY: offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = (event) => {
    if (!drag.current) {
      if (!draggable || !event.target.closest('.MuiDialogTitle-root')) return;
      setOverText(
        Boolean(event.target.closest('.MuiDialogTitle-root .MuiTypography-root'))
          || isOverText(event.target, event.clientX, event.clientY),
      );
      return;
    }
    setOffset({
      x: drag.current.baseX + event.clientX - drag.current.startX,
      y: drag.current.baseY + event.clientY - drag.current.startY,
    });
  };

  const handlePointerUp = (event) => {
    if (!drag.current) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const moved = offset.x !== 0 || offset.y !== 0;
  return (
    <Paper
      {...props}
      sx={[
        draggable && { '& .MuiDialogTitle-root': { cursor: overText ? 'text' : 'move' } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      style={moved ? { ...style, transform: `translate(${offset.x}px, ${offset.y}px)` } : style}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={() => setOverText(false)}
      data-draggable-dialog={draggable ? 'true' : 'false'}
    />
  );
}
